<template>
  <div id="app">
    <div class="container">
      <!-- 头部标题 -->
      <header class="app-header">
        <h1>🍌 Banana AI 图片处理器</h1>
        <p>使用AI技术为您的图片添加创意效果</p>
      </header>

      <!-- 主要内容区 -->
      <main class="app-main">
        <!-- 步骤1: 图片上传 -->
        <section class="card">
          <h2>📸 步骤1: 上传图片</h2>
          <ImageUpload @update:images="handleImagesUpdate" />
        </section>

        <!-- 步骤2: 选择操作 -->
        <section class="card">
          <h2>🎨 步骤2: 选择处理方式</h2>
          <OperationInput @update:operation="handleOperationUpdate" />
        </section>

        <!-- 处理按钮 -->
        <section class="process-section">
          <button
            :class="['btn', 'process-btn', { 'btn-loading': processing }]"
            :disabled="!canProcess || processing"
            @click="processImages"
          >
            <span v-if="processing" class="loading"></span>
            <span v-else>✨</span>
            {{ processing ? '处理中...' : '开始处理' }}
          </button>
          <p v-if="!canProcess" class="process-hint">
            {{ !selectedImages.length ? '请先上传图片' : '请选择处理方式' }}
          </p>
        </section>

              <!-- 步骤3: 处理结果 -->
      <section v-if="showResult" class="card result-section">
        <h2>🎯 步骤3: 处理结果</h2>
        <ResultDisplay
          :loading="processing"
          :error="error"
          :images="resultImages"
          @retry="processImages"
          @new-process="resetAll"
        />
      </section>

      <!-- 历史记录面板 -->
      <section class="card history-section">
        <HistoryPanel
          @reprocess="handleReprocess"
          @view-result="handleViewHistoryResult"
        />
      </section>
    </main>

      <!-- 页脚 -->
      <footer class="app-footer">
        <p>Powered by Banana AI • 让创意无限可能</p>
      </footer>
    </div>

    <!-- 全局通知 -->
    <div v-if="notification" :class="['notification', notification.type]">
      {{ notification.message }}
    </div>
  </div>
</template>

<script>
import ImageUpload from './components/ImageUpload.vue'
import OperationInput from './components/OperationInput.vue'
import ResultDisplay from './components/ResultDisplay.vue'
import HistoryPanel from './components/HistoryPanel.vue'
import { processImages as apiProcessImages } from './utils/api.js'
import { addHistoryRecord } from './utils/history.js'

export default {
  name: 'App',
  components: {
    ImageUpload,
    OperationInput,
    ResultDisplay,
    HistoryPanel
  },
  data () {
    return {
      selectedImages: [], // 选中的图片文件
      selectedOperation: '', // 选中的操作描述
      processing: false, // 是否正在处理
      showResult: false, // 是否显示结果区域
      resultImages: [], // 处理结果图片
      error: null, // 错误信息
      notification: null // 全局通知
    }
  },
  computed: {
    canProcess () {
      return this.selectedImages.length > 0 && this.selectedOperation.trim().length > 0
    }
  },
  methods: {
    handleImagesUpdate (images) {
      this.selectedImages = images.map(img => img.file)
      console.log('选中图片数量:', this.selectedImages.length)
    },
    handleOperationUpdate (operation) {
      this.selectedOperation = operation
      console.log('选中操作:', operation)
    },
    async processImages () {
      if (!this.canProcess) {
        this.showNotification('请先上传图片并选择处理方式', 'warning')
        return
      }

      this.processing = true
      this.showResult = true
      this.error = null
      this.resultImages = []

      try {
        console.log('开始处理图片...', {
          imageCount: this.selectedImages.length,
          operation: this.selectedOperation
        })

        // 使用更高的重试次数来应对API不稳定
        const result = await apiProcessImages(this.selectedOperation, this.selectedImages, 8)

        if (result.success) {
          this.resultImages = result.images
          this.showNotification(
            `🎉 成功生成 ${result.images.length} 张图片！`,
            'success'
          )
          console.log('处理成功:', result)

          // 保存成功的历史记录
          this.saveToHistory(result, true)
        } else {
          this.error = result.error

          // 如果是重试失败，显示更详细的错误信息
          let errorMsg = result.error
          if (result.retryCount > 1) {
            errorMsg += ` (已重试 ${result.retryCount} 次)`
          }

          this.showNotification('处理失败: ' + errorMsg, 'error')
          console.error('处理失败:', result)

          // 保存失败的历史记录
          this.saveToHistory(result, false)
        }
      } catch (error) {
        console.error('处理过程中发生错误:', error)
        this.error = error.message || '处理过程中发生未知错误'
        this.showNotification('处理失败: ' + this.error, 'error')
      } finally {
        this.processing = false
      }
    },
    resetAll () {
      this.selectedImages = []
      this.selectedOperation = ''
      this.processing = false
      this.showResult = false
      this.resultImages = []
      this.error = null
      this.showNotification('已重置，可以开始新的处理', 'info')
    },
    showNotification (message, type = 'info') {
      this.notification = { message, type }
      setTimeout(() => {
        this.notification = null
      }, 4000)
    },
    saveToHistory (result, success) {
      // 构建历史记录
      const record = {
        operation: this.selectedOperation,
        originalImages: this.selectedImages.map(file => ({
          name: file.name,
          size: file.size
        })),
        resultImages: success ? result.images : [],
        success
      }

      // 保存到localStorage
      addHistoryRecord(record)
    },
    handleReprocess (operation) {
      // 从历史记录中重新处理
      this.selectedOperation = operation
      this.showNotification('已加载历史操作，请选择图片后开始处理', 'info')

      // 滚动到操作输入区域
      document.querySelector('.card').scrollIntoView({ behavior: 'smooth' })
    },
    handleViewHistoryResult (images) {
      // 查看历史结果
      this.resultImages = images
      this.showResult = true
      this.showNotification('已加载历史结果', 'info')

      // 滚动到结果区域
      setTimeout(() => {
        document.querySelector('.result-section')?.scrollIntoView({ behavior: 'smooth' })
      }, 100)
    }
  }
}
</script>

<style>
/* 全局样式已在 global.css 中定义 */

.app-header {
  text-align: center;
  margin-bottom: 40px;
  color: white;
}

.app-header h1 {
  font-size: 2.5rem;
  margin-bottom: 8px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.app-header p {
  font-size: 1.1rem;
  opacity: 0.9;
}

.app-main {
  margin-bottom: 40px;
}

.app-main h2 {
  color: #2c3e50;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.process-section {
  text-align: center;
  margin: 32px 0;
}

.process-btn {
  font-size: 18px;
  padding: 16px 32px;
  min-width: 200px;
  position: relative;
}

.btn-loading {
  pointer-events: none;
}

.process-hint {
  margin-top: 12px;
  color: #6c757d;
  font-style: italic;
}

.result-section {
  animation: slideIn 0.5s ease-out;
}

.app-footer {
  text-align: center;
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  padding: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
}

.notification {
  position: fixed;
  top: 20px;
  right: 20px;
  padding: 16px 24px;
  border-radius: 8px;
  color: white;
  font-weight: 600;
  z-index: 1000;
  animation: slideInRight 0.3s ease-out;
  max-width: 400px;
  word-wrap: break-word;
}

.notification.success {
  background: #28a745;
}

.notification.error {
  background: #dc3545;
}

.notification.warning {
  background: #ffc107;
  color: #212529;
}

.notification.info {
  background: #17a2b8;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInRight {
  from {
    opacity: 0;
    transform: translateX(100%);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@media (max-width: 768px) {
  .app-header h1 {
    font-size: 2rem;
  }

  .process-btn {
    font-size: 16px;
    padding: 14px 28px;
    min-width: 180px;
  }

  .notification {
    right: 16px;
    left: 16px;
    max-width: none;
  }
}
</style>
